import { z } from 'zod';
import { IngestPlatform, MediaTicket } from './ingest';
import { Platform } from './platform';
import type { SelfNoteAnalysisDimensionCode } from './analysis';

// accounts 表的枚举真相源，与 infra/d1/migrations/shard/0002_shard_accounts.sql 的 CHECK 约束保持一致。
// 本期（S3）只落地 self 绑定，competitor/reference 留作后续；bind_status 全生命周期都先声明好。
export const AccountType = z.enum(['self', 'competitor', 'reference']);
export type AccountType = z.infer<typeof AccountType>;

export const AccountBindStatus = z.enum(['pending', 'verified', 'expired', 'revoked']);
export type AccountBindStatus = z.infer<typeof AccountBindStatus>;

// 自号绑定写入请求：插件读到的「本人创作者中心身份」。
// ⚠️ 不含 tenant_id：归属一律取自 JWT，请求体里任何租户字段都被忽略（PR#6 跨租户教训）。
// 故意不 .strict()——多带的字段（含 tenant_id）静默剥离即可，符合「一律忽略」而非「拒绝」。
export const AccountBindRequest = z.object({
  platform: IngestPlatform,
  // 平台账号 ID（小红书 userId 等），去重/幂等主键的一部分。
  external_id: z.string().min(1).max(128),
  // 平台 @ 用户名 / 小红书号、昵称、头像、简介——均为创作者中心可读的展示资料，可空。
  external_handle: z.string().max(128).nullable().optional(),
  display_name: z.string().max(128).nullable().optional(),
  avatar_url: z.string().max(2048).nullable().optional(),
  bio: z.string().max(512).nullable().optional(),
  followers_snapshot: z.number().int().nonnegative().nullable().optional(),
});
export type AccountBindRequest = z.infer<typeof AccountBindRequest>;

export const AccountBindingIntent = z.enum(['bind_sync', 'switch']);
export type AccountBindingIntent = z.infer<typeof AccountBindingIntent>;

export const AccountBindingChallengeRequest = z.object({
  platform: IngestPlatform,
  intent: AccountBindingIntent,
});
export type AccountBindingChallengeRequest = z.infer<typeof AccountBindingChallengeRequest>;

export type AccountBindingChallengeResponse = {
  code: string;
  platform: Platform;
  intent: AccountBindingIntent;
  expires_at: string;
  expected_account_id: string | null;
  expected_external_id: string | null;
  expected_binding_version: number | null;
};

export const AccountBindingConfirmRequest = AccountBindRequest.extend({
  code: z.string().min(32).max(256),
});
export type AccountBindingConfirmRequest = z.infer<typeof AccountBindingConfirmRequest>;

// 绑定成功后回传的账号视图（供前端 S5 绑定入口 + 状态展示复用）。
export type BoundAccount = {
  id: string;
  platform: Platform;
  external_id: string;
  external_handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  account_type: AccountType;
  bind_status: AccountBindStatus;
  bound_by_user_id: string | null;
  followers_snapshot: number | null;
  notes_snapshot: number | null;
  last_crawled_at: string | null;
  last_analyzed_at: string | null;
  created_at: string;
  updated_at: string;
};

// created = 新建一行 self/verified；refreshed = 同号重绑，刷新资料 + 续期（幂等收敛，不计配额、不新建）。
export type AccountBindResult = 'created' | 'refreshed' | 'switched';

export type PlatformAccountBinding = {
  platform: Platform;
  current_account_id: string;
  binding_version: number;
  identity_source: 'creator_center';
  first_bound_at: string;
  last_verified_at: string;
  last_switched_at: string | null;
  switch_locked_until: string | null;
  bound_by_user_id: string | null;
  updated_at: string;
};

export const AccountSyncRunStatus = z.enum([
  'pending',
  'running',
  'partial',
  'completed',
  'failed',
  'cancelled',
]);
export type AccountSyncRunStatus = z.infer<typeof AccountSyncRunStatus>;

export const AccountSyncSelectionMode = z.enum(['window_90d', 'recent_50']);
export type AccountSyncSelectionMode = z.infer<typeof AccountSyncSelectionMode>;

export const AccountSyncFailureCode = z.enum([
  'creator_identity_mismatch',
  'binding_version_stale',
  'creator_list_unavailable',
  'creator_detail_unavailable',
  'ingest_rejected',
  'quota_exceeded',
  'media_partial',
  'session_expired',
  'captcha_required',
  'rate_limited',
  'upload_retry_exhausted',
  'unknown',
]);
export type AccountSyncFailureCode = z.infer<typeof AccountSyncFailureCode>;

export const ACCOUNT_SYNC_DETAIL_MAX_ATTEMPTS = 3;

export const ACCOUNT_SYNC_PAUSE_FAILURE_CODES = [
  'creator_identity_mismatch',
  'binding_version_stale',
  'quota_exceeded',
  'session_expired',
  'captcha_required',
  'rate_limited',
  'upload_retry_exhausted',
] as const satisfies readonly AccountSyncFailureCode[];

export function accountSyncFailurePausesRun(code: AccountSyncFailureCode): boolean {
  return ACCOUNT_SYNC_PAUSE_FAILURE_CODES.includes(
    code as (typeof ACCOUNT_SYNC_PAUSE_FAILURE_CODES)[number],
  );
}

export const AccountSyncFailureStage = z.enum([
  'snapshot',
  'open',
  'read',
  'parse',
  'body_upload',
  'media_download',
  'media_upload',
  'finalize',
  'progress',
]);
export type AccountSyncFailureStage = z.infer<typeof AccountSyncFailureStage>;

export const AccountSyncFailureKind = z.enum([
  'global_block',
  'upload_block',
  'deterministic',
  'binding_stale',
]);
export type AccountSyncFailureKind = z.infer<typeof AccountSyncFailureKind>;

/**
 * 失败分类由共享合同统一拥有，API、Ingest 和扩展不得各自维护错误码表。
 * 未知错误默认是当前单篇确定性失败，避免队列无限重新打开同一页面。
 */
export function classifyAccountSyncFailure(
  code: AccountSyncFailureCode,
  stage: AccountSyncFailureStage | null,
): AccountSyncFailureKind {
  if (code === 'binding_version_stale') return 'binding_stale';
  if (code === 'upload_retry_exhausted') return 'upload_block';
  if (
    [
      'creator_identity_mismatch',
      'quota_exceeded',
      'session_expired',
      'captcha_required',
      'rate_limited',
    ].includes(code)
  ) {
    return 'global_block';
  }
  if (
    stage &&
    ['body_upload', 'media_download', 'media_upload', 'finalize', 'progress'].includes(stage) &&
    code === 'ingest_rejected'
  ) {
    return 'deterministic';
  }
  return 'deterministic';
}

export const AccountSyncBranchStatus = z.enum(['pending', 'running', 'succeeded', 'failed']);
export type AccountSyncBranchStatus = z.infer<typeof AccountSyncBranchStatus>;

export const NoteDetailSyncStatus = z.enum(['pending', 'leased', 'succeeded', 'failed']);
export type NoteDetailSyncStatus = z.infer<typeof NoteDetailSyncStatus>;

export const CreatorSyncCandidate = z
  .object({
    external_id: z.string().min(1).max(128),
    xsec_token: z.string().min(1).max(2048),
    xsec_source: z.string().min(1).max(128),
    title: z.string().max(512).nullable(),
    content_type: z.enum(['text', 'image', 'video', 'mixed']),
    cover_url: z.string().url().max(2048).nullable(),
    published_external_at: z.string().nullable(),
    engagement: z
      .object({
        views: z.number().int().nonnegative().nullable(),
        likes: z.number().int().nonnegative().nullable(),
        comments: z.number().int().nonnegative().nullable(),
        collects: z.number().int().nonnegative().nullable(),
        shares: z.number().int().nonnegative().nullable(),
      })
      .strict(),
  })
  .strict();
export type CreatorSyncCandidate = z.infer<typeof CreatorSyncCandidate>;

export const AccountDailyMetric = z
  .object({
    period_date: z.string().date(),
    impressions: z.number().int().nonnegative().nullable(),
    views: z.number().int().nonnegative().nullable(),
    likes: z.number().int().nonnegative().nullable(),
    comments: z.number().int().nonnegative().nullable(),
    collects: z.number().int().nonnegative().nullable(),
    shares: z.number().int().nonnegative().nullable(),
    notes_published: z.number().int().nonnegative().nullable(),
    profile_visits: z.number().int().nonnegative().nullable(),
    followers_gained: z.number().int().nonnegative().nullable(),
    followers_lost: z.number().int().nonnegative().nullable(),
    followers_net: z.number().int().nullable(),
    cover_click_rate: z.number().nonnegative().nullable(),
    watch_duration_ms: z.number().nonnegative().nullable(),
    video_completion_rate: z.number().nonnegative().nullable(),
  })
  .strict();
export type AccountDailyMetric = z.infer<typeof AccountDailyMetric>;

export const ACCOUNT_DAILY_METRICS_DEFAULT_RANGE_DAYS = 30;
export const ACCOUNT_DAILY_METRICS_MAX_RANGE_DAYS = 90;
export const ACCOUNT_DAILY_METRICS_FRESHNESS_GRACE_DAYS = 2;

export const AccountDailyMetricsQuery = z
  .object({
    platform: Platform,
    from: z.string().date().optional(),
    to: z.string().date().optional(),
  })
  .superRefine((query, ctx) => {
    if ((query.from && !query.to) || (!query.from && query.to)) {
      ctx.addIssue({
        code: 'custom',
        path: query.from ? ['to'] : ['from'],
        message: 'from 与 to 必须同时提供',
      });
      return;
    }
    if (!query.from || !query.to) return;

    const from = Date.parse(`${query.from}T00:00:00.000Z`);
    const to = Date.parse(`${query.to}T00:00:00.000Z`);
    if (from > to) {
      ctx.addIssue({
        code: 'custom',
        path: ['from'],
        message: 'from 不能晚于 to',
      });
      return;
    }
    const rangeDays = Math.floor((to - from) / 86_400_000) + 1;
    if (rangeDays > ACCOUNT_DAILY_METRICS_MAX_RANGE_DAYS) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `单次最多查询 ${ACCOUNT_DAILY_METRICS_MAX_RANGE_DAYS} 天`,
      });
    }
  });
export type AccountDailyMetricsQuery = z.infer<typeof AccountDailyMetricsQuery>;

export const AccountDailyMetricsStatus = z.enum(['fresh', 'stale', 'partial', 'empty']);
export type AccountDailyMetricsStatus = z.infer<typeof AccountDailyMetricsStatus>;

export const AccountDailyMetricReadPoint = AccountDailyMetric.extend({
  source_sync_run_id: z.string().uuid(),
  updated_at: z.string().datetime(),
}).strict();
export type AccountDailyMetricReadPoint = z.infer<typeof AccountDailyMetricReadPoint>;

export const AccountDailyMetricsResponse = z
  .object({
    account_id: z.string().min(1),
    platform: Platform,
    binding_version: z.number().int().positive(),
    range: z
      .object({
        from: z.string().date(),
        to: z.string().date(),
        days: z.number().int().min(1).max(ACCOUNT_DAILY_METRICS_MAX_RANGE_DAYS),
      })
      .strict(),
    latest_metric_date: z.string().date().nullable(),
    last_updated_at: z.string().datetime().nullable(),
    coverage: z
      .object({
        covered_days: z.number().int().nonnegative(),
        missing_days: z.number().int().nonnegative(),
      })
      .strict(),
    status: AccountDailyMetricsStatus,
    metrics: z.array(AccountDailyMetricReadPoint).max(ACCOUNT_DAILY_METRICS_MAX_RANGE_DAYS),
  })
  .strict();
export type AccountDailyMetricsResponse = z.infer<typeof AccountDailyMetricsResponse>;

const AccountSyncAuthority = z.object({
  account_id: z.string().uuid(),
  account_binding_version: z.number().int().positive(),
});

export const AccountSyncRunCreateRequest = z
  .object({
    request_id: z.string().uuid().optional(),
  })
  .strict();
export type AccountSyncRunCreateRequest = z.infer<typeof AccountSyncRunCreateRequest>;

export const AccountSyncSnapshotRequest = AccountSyncAuthority.extend({
  request_id: z.string().uuid().optional(),
  candidates: z.array(CreatorSyncCandidate).min(1).max(50),
}).strict();
export type AccountSyncSnapshotRequest = z.infer<typeof AccountSyncSnapshotRequest>;

export const AccountSyncSnapshotResponse = z
  .object({
    accepted: z.number().int().nonnegative(),
    updated_at: z.string().datetime(),
    replayed: z.boolean(),
  })
  .strict();
export type AccountSyncSnapshotResponse = z.infer<typeof AccountSyncSnapshotResponse>;

export const AccountSyncMetricsRequest = AccountSyncAuthority.extend({
  metrics: z.array(AccountDailyMetric).min(1).max(30),
}).strict();
export type AccountSyncMetricsRequest = z.infer<typeof AccountSyncMetricsRequest>;

export const AccountSyncBranchFailureRequest = AccountSyncAuthority.extend({
  branch: z.enum(['list', 'metrics']),
  failure_code: AccountSyncFailureCode,
}).strict();
export type AccountSyncBranchFailureRequest = z.infer<typeof AccountSyncBranchFailureRequest>;

export const AccountSyncLeaseRequest = AccountSyncAuthority.extend({
  instance_id: z.string().min(1).max(128),
  preferred_task_id: z.string().uuid().optional(),
}).strict();
export type AccountSyncLeaseRequest = z.infer<typeof AccountSyncLeaseRequest>;

export const AccountSyncLease = z.object({
  lease_id: z.string().uuid(),
  task_id: z.string().uuid(),
  sync_run_id: z.string().uuid(),
  external_id: z.string().min(1),
  xsec_token: z.string().min(1),
  xsec_source: z.string().min(1),
  preview_url: z.string().url(),
  attempts: z.number().int().nonnegative(),
  lease_expires_at: z.string().datetime(),
});
export type AccountSyncLease = z.infer<typeof AccountSyncLease>;

export const AccountSyncLeaseResponse = z.object({
  lease: AccountSyncLease.nullable(),
  /** 当前没有可领取 lease 时，服务端是否仍有 pending/leased 任务。 */
  has_more: z.boolean().optional(),
});
export type AccountSyncLeaseResponse = z.infer<typeof AccountSyncLeaseResponse>;

export const AccountSyncLeaseRenewRequest = AccountSyncAuthority.extend({
  lease_id: z.string().uuid(),
  instance_id: z.string().min(1).max(128),
}).strict();
export type AccountSyncLeaseRenewRequest = z.infer<typeof AccountSyncLeaseRenewRequest>;

export const AccountSyncLeaseRenewResponse = z
  .object({
    ok: z.literal(true),
    lease_expires_at: z.string().datetime(),
  })
  .strict();
export type AccountSyncLeaseRenewResponse = z.infer<typeof AccountSyncLeaseRenewResponse>;

export const AccountSyncTaskFailureRequest = AccountSyncAuthority.extend({
  lease_id: z.string().uuid(),
  failure_code: AccountSyncFailureCode,
  failure_message: z.string().max(512).nullable(),
  failure_stage: AccountSyncFailureStage.nullable().optional(),
  upload_attempts: z.number().int().nonnegative().max(3).optional(),
  pause_run: z.boolean(),
}).strict();
export type AccountSyncTaskFailureRequest = z.infer<typeof AccountSyncTaskFailureRequest>;

export const AccountSyncTaskFailureDisposition = z.enum(['failed', 'paused']);
export type AccountSyncTaskFailureDisposition = z.infer<typeof AccountSyncTaskFailureDisposition>;

export const AccountSyncTaskFailureResponse = z
  .object({
    ok: z.literal(true),
    disposition: AccountSyncTaskFailureDisposition,
    task_status: z.enum(['pending', 'failed', 'cancelled']),
    attempts: z.number().int().nonnegative(),
    paused: z.boolean(),
    has_more: z.boolean(),
    updated_at: z.string().datetime(),
  })
  .strict();
export type AccountSyncTaskFailureResponse = z.infer<typeof AccountSyncTaskFailureResponse>;

export const AccountSyncDetailTaskStatus = z.enum([
  'pending',
  'leased',
  'succeeded',
  'failed',
  'cancelled',
]);
export type AccountSyncDetailTaskStatus = z.infer<typeof AccountSyncDetailTaskStatus>;

export const AccountSyncDetailTask = z
  .object({
    id: z.string().uuid(),
    sync_run_id: z.string().uuid(),
    external_id: z.string().min(1),
    title: z.string().max(512).nullable(),
    content_type: z.enum(['text', 'image', 'video', 'mixed']),
    status: AccountSyncDetailTaskStatus,
    attempts: z.number().int().nonnegative(),
    note_id: z.string().nullable(),
    body_saved_at: z.string().min(1).nullable(),
    failure_code: AccountSyncFailureCode.nullable(),
    failure_message: z.string().nullable(),
    failure_stage: AccountSyncFailureStage.nullable(),
    upload_attempts: z.number().int().nonnegative(),
    lease_expires_at: z.string().min(1).nullable(),
    completed_at: z.string().min(1).nullable(),
    created_at: z.string().min(1),
    updated_at: z.string().min(1),
  })
  .strict();
export type AccountSyncDetailTask = z.infer<typeof AccountSyncDetailTask>;

export const AccountSyncContinueResponse = z.object({ ok: z.literal(true) }).strict();
export type AccountSyncContinueResponse = z.infer<typeof AccountSyncContinueResponse>;

export const ManualSelfAnalysisRequest = z
  .object({
    model_id: z.string().min(1),
    confirmed: z.literal(true),
  })
  .strict();
export type ManualSelfAnalysisRequest = z.infer<typeof ManualSelfAnalysisRequest>;

export type AccountSyncRun = {
  id: string;
  platform: IngestPlatform;
  account_id: string;
  binding_version: number;
  status: AccountSyncRunStatus;
  selection_mode: AccountSyncSelectionMode | null;
  selected_total: number;
  succeeded_count: number;
  partial_count: number;
  failed_count: number;
  failure_code: AccountSyncFailureCode | null;
  list_status?: AccountSyncBranchStatus;
  metrics_status?: AccountSyncBranchStatus;
  detail_status?: AccountSyncBranchStatus;
  paused_reason?: AccountSyncFailureCode | null;
  snapshot_request_id?: string | null;
  snapshot_payload_hash?: string | null;
  snapshot_received_at?: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  /** 当前同步批次允许提交的最大笔记数；旧服务端响应缺失时由扩展回退到 50。 */
  batch_limit?: number;
};

export type AccountSyncLatestRunResponse = {
  run: AccountSyncRun | null;
  tasks: AccountSyncDetailTask[];
  has_unsettled_tasks: boolean;
};

export const AccountSyncUploadStateRequest = AccountSyncAuthority.extend({
  lease_id: z.string().uuid(),
}).strict();
export type AccountSyncUploadStateRequest = z.infer<typeof AccountSyncUploadStateRequest>;

export const AccountSyncMediaUploadState = z
  .object({
    position: z.number().int().nonnegative(),
    media_id: z.string().min(1),
    status: z.enum(['stored', 'missing']),
  })
  .strict();
export type AccountSyncMediaUploadState = z.infer<typeof AccountSyncMediaUploadState>;

export const AccountSyncUploadStateResponse = z
  .object({
    body_saved: z.boolean(),
    note_id: z.string().nullable(),
    media: z.array(AccountSyncMediaUploadState),
    media_tickets: z.array(MediaTicket),
  })
  .strict();
export type AccountSyncUploadStateResponse = z.infer<typeof AccountSyncUploadStateResponse>;

export const AccountSyncFinalizeRequest = AccountSyncAuthority.extend({
  lease_id: z.string().uuid(),
  note_id: z.string().min(1),
}).strict();
export type AccountSyncFinalizeRequest = z.infer<typeof AccountSyncFinalizeRequest>;

export const AccountSyncFinalizeResponse = z
  .object({
    ok: z.literal(true),
    replayed: z.boolean(),
    note_id: z.string().min(1),
    task_status: z.literal('succeeded'),
    run_status: AccountSyncRunStatus,
  })
  .strict();
export type AccountSyncFinalizeResponse = z.infer<typeof AccountSyncFinalizeResponse>;

export const AccountSyncProgressRequest = z
  .object({
    account_id: z.string().min(1),
    account_binding_version: z.number().int().positive(),
    status: AccountSyncRunStatus,
    selection_mode: AccountSyncSelectionMode.nullable(),
    selected_total: z.number().int().nonnegative(),
    succeeded_count: z.number().int().nonnegative(),
    partial_count: z.number().int().nonnegative(),
    failed_count: z.number().int().nonnegative(),
    failure_code: AccountSyncFailureCode.nullable(),
  })
  .strict();
export type AccountSyncProgressRequest = z.infer<typeof AccountSyncProgressRequest>;

export type AccountBindResponse = {
  account: BoundAccount;
  result: AccountBindResult;
};

export type AccountBindingConfirmResponse = AccountBindResponse & {
  binding: PlatformAccountBinding;
  sync_run: AccountSyncRun;
};

// —— 首次回灌（ME-31 切片 2）——
// 回灌生命周期：pending（待开始/无可选笔记）→ running（多日分批入队中）→ done（全部入队完成）。
export const BackfillStatus = z.enum(['pending', 'running', 'done']);
export type BackfillStatus = z.infer<typeof BackfillStatus>;

// 选片模式（决策 2）：近 90 天内为主；该窗口 0 篇时回退取最近 50 篇（倒序）。
export const BackfillMode = z.enum(['window_90d', 'recent_50']);
export type BackfillMode = z.infer<typeof BackfillMode>;

// 每账号每日入队硬顶（回灌与增量共享同一计数）。
export const BACKFILL_DAILY_CAP = 200;

// 回灌进度视图（供前端 S5 显示 + 切片 3 状态查询复用）。
// daily_cap = 每账号每日入队硬顶（回灌与增量共享）；enqueued_today 由 crawl_targets 当日计数派生。
export type BackfillProgress = {
  status: BackfillStatus;
  mode: BackfillMode | null;
  selected_total: number;
  enqueued_count: number;
  daily_cap: number;
  enqueued_today: number;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string | null;
};

export type AccountStatusItem = BoundAccount & {
  binding: PlatformAccountBinding;
  backfill: BackfillProgress;
};

export type AccountsStatusResponse = {
  items: AccountStatusItem[];
};

export const AccountPlatformQuery = z.object({ platform: Platform });
export type AccountPlatformQuery = z.infer<typeof AccountPlatformQuery>;

export type AccountHealthSnapshot = {
  period_date: string;
  followers: number | null;
  followers_delta_1d: number | null;
  notes_total: number | null;
  notes_published_1d: number | null;
  total_likes_1d: number | null;
  total_collects_1d: number | null;
  total_comments_1d: number | null;
  total_shares_1d: number | null;
  health_score: number | null;
  risk_flags: string[];
  updated_at: string;
};

export type AccountDiagnosisFunnel = {
  impressions: number;
  reads: number;
  interactions: number;
  exposure_to_read_rate: number | null;
  read_to_interaction_rate: number | null;
  notes_with_metrics: number;
  period_days: number;
};

export type AccountFollowerSources = {
  search: number;
  feed: number;
  follow_page: number;
  profile: number;
  other: number;
  total: number;
  net_delta: number;
};

export type AccountDiagnosisReadinessStatus =
  | 'syncing'
  | 'partial'
  | 'insufficient_data'
  | 'ready'
  | 'failed';

export type AccountDiagnosisMediaStatus = 'empty' | 'syncing' | 'partial' | 'ready';

export type AccountDiagnosisContentInsight = {
  note_id: string;
  title: string | null;
  source_url: string | null;
  published_at: string | null;
  body_available: boolean;
  media_count: number;
  uploaded_media_count: number;
  analysis_status: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';
  summary: string | null;
  score: number | null;
  originality: number | null;
  engagement: Record<string, unknown>;
  detail: import('./analysis').SelfNoteAnalysisInsight | null;
};

export type AccountDiagnosisPattern = {
  dimension: 'topic' | 'hook_style' | 'structure' | 'cta_style';
  value: string;
  note_count: number;
  exposure_to_read_rate: number | null;
  read_to_interaction_rate: number | null;
  follower_delta: number;
  sample_note_ids: string[];
};

export type AccountDiagnosisRecurringFinding = {
  dimension_code: SelfNoteAnalysisDimensionCode;
  dimension: string;
  count: number;
  evidence: string[];
};

export type AccountDiagnosisBenchmark = {
  value: number;
  source: 'account' | 'reference';
  sample_size: number;
};

export type AccountDiagnosisTrend = {
  direction: 'up' | 'flat' | 'down' | 'insufficient_data';
  current: number | null;
  previous: number | null;
  delta: number | null;
  sample_size: number;
};

export type AccountDiagnosisResponse = {
  account: BoundAccount;
  health: {
    latest: AccountHealthSnapshot | null;
    trend_90d: AccountHealthSnapshot[];
  };
  funnel: AccountDiagnosisFunnel;
  benchmarks: {
    read_rate: AccountDiagnosisBenchmark;
    interaction_rate: AccountDiagnosisBenchmark;
  };
  trends: {
    health: AccountDiagnosisTrend;
    cadence: AccountDiagnosisTrend;
  };
  follower_sources: AccountFollowerSources;
  readiness: {
    status: AccountDiagnosisReadinessStatus;
    latest_sync_run: AccountSyncRun | null;
    notes_total: number;
    notes_analyzed: number;
    notes_with_body: number;
    notes_with_metrics: number;
    media: {
      status: AccountDiagnosisMediaStatus;
      expected_count: number;
      uploaded_count: number;
      partial_note_count: number;
    };
  };
  content: {
    insights: AccountDiagnosisContentInsight[];
    high_performance_patterns: AccountDiagnosisPattern[];
    low_performance_patterns: AccountDiagnosisPattern[];
    recurring_strengths: AccountDiagnosisRecurringFinding[];
    recurring_issues: AccountDiagnosisRecurringFinding[];
  };
};
